import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosApiInstance } from "@/lib/axios";
import { API_PORTAL_URL } from "@/lib/config";

export const getHomeDetails = createAsyncThunk("Website/HomePage", async () => {
  try {
    const response = await axiosApiInstance.get(
      `${API_PORTAL_URL}/Website/HomePage`,
    );
    return response.data?.Data;
  } catch (error: any) {
    console.error("Error Fetching Home Details:", error);
    return Promise.reject({
      message: error?.response?.data?.Details || "Something went wrong",
    });
  }
});

export const getGeneralPagesDetails = createAsyncThunk(
  "Frontend/General",
  async (URL: string) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/Frontend/General`,
        { URL },
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Page Details:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveSettings = createAsyncThunk(
  "Setting/FindAllActiveSetting",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Setting/FindAllActiveSetting`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching settings:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllMetaData = createAsyncThunk(
  "SeoTags/FindAllSeoTags",
  async (FormData: any) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/SeoTags/FindAllSeoTags`,
        FormData,
      );
      return response.data?.Data ? response.data?.Data[0] : {};
    } catch (error: any) {
      console.error("Error Fetching Meta Data:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveTestimonials = createAsyncThunk(
  "/Testimonial/FindAllActiveTestimonial",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Testimonial/FindAllActiveTestimonial`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching testimonials:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveSectors = createAsyncThunk(
  "/Sector/FindAllActiveSector",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Sector/FindAllActiveSector`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Sectors:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllCMSPageData = createAsyncThunk(
  "CMS/FindByURLCMS",
  async (URL: string) => {
    try {
      const response = await axiosApiInstance.post(
        `${API_PORTAL_URL}/CMS/FindByURLCMS`,
        { CMSURL: URL },
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Page Details:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getAllActiveBanner = createAsyncThunk(
  "Banner/FindAllActiveBanner",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Banner/FindAllActiveBanner`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Banner:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

export const getHeaderMenuData = createAsyncThunk(
  "Website/HeaderMenuData",
  async () => {
    try {
      const response = await axiosApiInstance.get(
        `${API_PORTAL_URL}/Website/HeaderMenuData`,
      );
      return response.data?.Data;
    } catch (error: any) {
      console.error("Error Fetching Header Menu Data:", error);
      return Promise.reject({
        message: error?.response?.data?.Details || "Something went wrong",
      });
    }
  },
);

// ─── SERVER (native fetch — call ONLY from Server Components / page.tsx) ────

export async function fetchHomePageData() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Website/HomePage`, {
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data ?? null;
  } catch {
    return null;
  }
}

export async function fetchMetaData(PageURL: string) {
  try {
    const res = await fetch(`${API_PORTAL_URL}/SeoTags/FindAllSeoTags`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ PageName: "", PageURL: PageURL }),
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data?.[0] ?? null;
  } catch {
    return null;
  }
}

export async function getSeoMetadata(slug: string) {
  return fetchMetaData(slug);
}

export async function fetchCMSData(URL: string) {
  try {
    const res = await fetch(`${API_PORTAL_URL}/CMS/FindByURLCMS`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ CMSURL: URL }),
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}
export async function fetchAllActiveTestimonials() {
  try {
    const res = await fetch(
      `${API_PORTAL_URL}/Testimonial/FindAllActiveTestimonial`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        next: { revalidate: 300 },
      },
    );
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchSectors() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Sector/FindAllActiveSector`, {
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data || [];
  } catch {
    return [];
  }
}

export async function fetchAllActiveSettings() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Setting/FindAllActiveSetting`, {
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchAllActiveBanners() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Banner/FindAllActiveBanner`, {
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data ?? [];
  } catch {
    return [];
  }
}

export async function fetchHeaderMenuData() {
  try {
    const res = await fetch(`${API_PORTAL_URL}/Website/HeaderMenuData`, {
      next: { revalidate: 300 },
    });
    const json = await res.json();
    return json.Data ?? null;
  } catch {
    return null;
  }
}

export async function fetchActiveSpecialDates() {
  try {
    const res = await fetch(
      `${API_PORTAL_URL}/SpecialDates/FindAllActiveSpecialDates`,
      {
        next: { revalidate: 300 },
      },
    );
    const json = await res.json();
    return json.Data ?? [];
  } catch (error) {
    console.error("Error fetching ActiveSpecialDates:", error);
    return [];
  }
}
